/**
 * HeroExploreForm — the dual "Choose a Place × Choose a Topic" selector
 * with the primary Explore CTA. Renders as two rounded pills separated
 * by an "×" glyph, then a full-width blue button.
 *
 * On submit, navigates to `/explore?place=<slug>&topic=<slug>` — the
 * existing Explore route already understands these query params.
 */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, MapPin, Layers } from "lucide-react";

const POPULAR_PLACES = [
  { value: "kerala",       label: "Kerala, India" },
  { value: "mumbai",       label: "Mumbai, India" },
  { value: "bengaluru",    label: "Bengaluru, India" },
  { value: "new-york",     label: "New York, USA" },
  { value: "london",       label: "London, UK" },
  { value: "dubai",        label: "Dubai, UAE" },
  { value: "singapore",    label: "Singapore" },
];

const POPULAR_TOPICS = [
  { value: "housing",       label: "Housing & Shelter" },
  { value: "food",          label: "Food & Basics" },
  { value: "education",     label: "Education" },
  { value: "healthcare",    label: "Healthcare" },
  { value: "senior-care",   label: "Senior Care" },
  { value: "civic",         label: "Civic Issues" },
  { value: "community",     label: "Community Events" },
  { value: "family",        label: "Family & Genealogy" },
];

export default function HeroExploreForm() {
  const nav = useNavigate();
  const [place, setPlace] = useState("");
  const [topic, setTopic] = useState("");

  function submit(e) {
    e.preventDefault();
    const qs = new URLSearchParams();
    if (place) qs.set("place", place);
    if (topic) qs.set("topic", topic);
    nav(`/explore${qs.toString() ? "?" + qs.toString() : ""}`);
  }

  return (
    <form onSubmit={submit} className="space-y-3" data-testid="sp-hero-form">
      <div className="flex items-center gap-2">
        <div className="flex-1">
          <SelectPill
            testid="sp-place-select"
            icon={<MapPin className="w-4 h-4 text-slate-400" />}
            value={place}
            onChange={setPlace}
            options={POPULAR_PLACES}
            placeholder="Choose a Place"
          />
        </div>
        <span className="text-slate-400 text-lg select-none px-1">×</span>
        <div className="flex-1">
          <SelectPill
            testid="sp-topic-select"
            icon={<Layers className="w-4 h-4 text-slate-400" />}
            value={topic}
            onChange={setTopic}
            options={POPULAR_TOPICS}
            placeholder="Choose a Topic"
          />
        </div>
      </div>
      <button
        type="submit"
        data-testid="sp-explore-btn"
        className="w-full inline-flex items-center justify-center gap-2 h-14 rounded-2xl bg-blue-600 hover:bg-blue-700 text-white font-semibold text-base shadow-md transition-colors"
      >
        Explore <ArrowRight className="w-5 h-5" />
      </button>
    </form>
  );
}

function SelectPill({ testid, icon, value, onChange, options, placeholder }) {
  return (
    <div className="relative">
      <div className="absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none">
        {icon}
      </div>
      <select
        data-testid={testid}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full h-14 pl-11 pr-4 rounded-2xl border border-slate-200 bg-white text-slate-800 text-sm font-medium appearance-none cursor-pointer hover:border-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
      >
        <option value="">{placeholder}</option>
        {options.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
      <span className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-slate-400">▾</span>
    </div>
  );
}
